/**
 * The overview's loading shape.
 *
 * Shaped like the real page rather than a generic grid (P-05), so nothing jumps
 * when the figures arrive. It is used twice — as the `Suspense` fallback for the
 * page's lazy chunk, and as the first-load state inside it — which is why it
 * lives apart from both.
 *
 * The filter bar is *not* part of it: the filter renders outside the boundaries
 * and stays interactive throughout, so drawing a placeholder for it would be
 * drawing a placeholder for something already on screen.
 */
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/** Mirrors the KPI strip's 1 / 2 / 3 / 5 column ladder exactly. */
export const KpiStripSkeleton = () => (
	<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
		{Array.from({ length: 5 }, (_, index) => (
			<Skeleton key={index} className="h-[104px] w-full rounded-xl" />
		))}
	</div>
)

/** The most rows the endpoint ever sends, and so the tallest the card gets. */
const RECENT_CASE_ROWS = 10

/**
 * The Recent Cases card while its catalog lookup resolves.
 *
 * Shown *inside* the section rather than in place of it: the payload has
 * already arrived by then and only the integration names are still in flight,
 * so this stands in for the table alone. Its height is the card's real chrome
 * plus ten rows of the table's own row height — measured rather than guessed,
 * so the card does not grow under the reader when the names land.
 */
export const RecentCasesSkeleton = () => (
	<Card className="h-full gap-4">
		<CardHeader>
			<Skeleton className="h-5 w-36" />
			<Skeleton className="h-4 w-56" />
		</CardHeader>

		<CardContent className="flex flex-col gap-2">
			{/* A header row and ten data rows, each a row-height's worth of the
			    table it stands in for. */}
			<Skeleton className="h-10 w-full" />
			{Array.from({ length: RECENT_CASE_ROWS }, (_, index) => (
				<Skeleton key={index} className="h-10 w-full" />
			))}
		</CardContent>
	</Card>
)

export const OverviewSkeleton = () => (
	<div className="flex w-full flex-col gap-4">
		<KpiStripSkeleton />

		{/* Mirrors the card row's 2-of-5 / 3-of-5 split. */}
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
			<Skeleton className="h-[320px] w-full rounded-xl lg:col-span-2" />
			<Skeleton className="h-[320px] w-full rounded-xl lg:col-span-3" />
		</div>

		{/* The whole page is loading here, so the card's own chrome is a
		    placeholder too — a plain block, not the table-shaped skeleton the
		    section falls back to once the payload has landed. */}
		<Skeleton className="h-[42rem] w-full rounded-xl" />
	</div>
)
